/*
 * PartyKit network client. Manages the WebSocket lifecycle and translates
 * between the wire protocol (in /shared) and game events.
 *
 * Boot flow:
 *  1. Constructor reads the current sessionToken (if any) and opens a socket
 *     via `partysocket` (which handles reconnect with backoff for us).
 *  2. On socket open we send `client_hello`. The server replies with
 *     `server_welcome` + `server_room_state` (or `server_error` and a close).
 *  3. From then on we forward parsed messages to `onServerMessage` and accept
 *     `sendMove / sendChat / sendCosmetic / sendScore` calls from the game.
 *
 * Offline tolerance:
 *  If the server is unreachable, `partysocket` keeps retrying in the background
 *  and the status stays 'reconnecting'. The game continues to run; AI NPCs fill
 *  the world. Sent messages while disconnected are dropped silently.
 *
 * Move throttle:
 *  The game calls `sendMove(x, y)` whenever it wants the local position on the
 *  wire. We coalesce to one send every MOVE_INTERVAL_MS (matches the 10 Hz
 *  cadence specified in the protocol). The latest pending position is sent on
 *  each interval; if the player isn't moving, no send.
 */

import PartySocket from 'partysocket';
import {
  PROTOCOL_VERSION,
  type ClientChat,
  type ClientCosmeticUpdate,
  type ClientEmote,
  type ClientHello,
  type ClientMinigameScore,
  type ClientMove,
  type ClientPing,
  type EmoteName,
  type ServerMessage,
} from '../../../shared/protocol';
import { clearSessionToken, readSessionToken, writeSessionToken } from '@/net/session';

/** 10 Hz position cadence. */
const MOVE_INTERVAL_MS = 100;

/** Heartbeat cadence. */
const PING_INTERVAL_MS = 15_000;

/**
 * Lifecycle status surfaced to the game so it can spawn AI fallbacks
 * appropriately and (eventually) show a soft connection indicator.
 */
export type NetStatus =
  | 'idle'           // not started yet
  | 'connecting'     // socket open in flight
  | 'connected'      // hello/welcome handshake complete
  | 'reconnecting'   // socket dropped; partysocket is retrying
  | 'offline';       // explicit disconnect or unrecoverable

export interface NetClientOptions {
  /** Override the PartyKit host (e.g. 'fernhollow-square.foo.partykit.dev'). */
  host?: string;
  /** Room id within the party. Defaults to a single 'main' square. */
  room?: string;
  /** Party (matches partykit.json `name`). */
  party?: string;
  /** Initial critter payload — server validates indices. */
  initialCritter: { name: string; color: number; hat: number };
  /** Fired for every parsed server message. The game dispatches on `type`. */
  onServerMessage: (msg: ServerMessage) => void;
  /** Fired whenever the connection status changes. */
  onStatusChange: (status: NetStatus) => void;
}

export interface NetClient {
  status(): NetStatus;
  sendMove(x: number, y: number): void;
  sendChat(phraseIndex: number): void;
  sendCosmetic(color: number, hat: number): void;
  sendScore(minigame: 'forage' | 'fish', score: number): void;
  sendEmote(name: EmoteName): void;
  disconnect(): void;
}

/**
 * PartyKit party name. When `partykit.json` declares just a `main` entry
 * (no explicit `parties` mapping), the party is registered under the name
 * `'main'`. The party name has to match what the server registered; getting
 * this wrong is a silent failure (client falls into offline mode).
 *
 * If we later add a `parties` mapping to partykit.json with an explicit name,
 * change this string to match.
 */
const DEFAULT_PARTY = 'main';
const DEFAULT_ROOM = 'main';

/**
 * Decide what host to point at.
 * - VITE_PARTYKIT_HOST env var wins if set (production: 'foo.partykit.dev').
 * - In dev (vite serves the client on :5173), default to localhost:1999 — the
 *   port `partykit dev` uses by default.
 * - Otherwise, undefined → partysocket throws and we go straight to offline.
 */
function resolveHost(override: string | undefined): string | undefined {
  if (override) return override;
  const envHost = (import.meta.env['VITE_PARTYKIT_HOST'] as string | undefined) ?? undefined;
  if (envHost) return envHost;
  if (import.meta.env.DEV) return 'localhost:1999';
  return undefined;
}

export function createNetClient(opts: NetClientOptions): NetClient {
  const host = resolveHost(opts.host);
  let status: NetStatus = 'idle';

  function setStatus(next: NetStatus): void {
    if (status === next) return;
    status = next;
    opts.onStatusChange(status);
  }

  if (!host) {
    setStatus('offline');
    return offlineStub();
  }

  // Partysocket handles reconnect with exponential backoff and clean lifecycle
  // events. We just react to its callbacks.
  const socket = new PartySocket({
    host,
    room: opts.room ?? DEFAULT_ROOM,
    party: opts.party ?? DEFAULT_PARTY,
  });

  // Hello timing. The protocol spec says the FIRST message on every socket is
  // `client_hello`. We send it on 'open' and again on any reopen.
  function sendHello(): void {
    const token = readSessionToken();
    const hello: ClientHello = {
      type: 'client_hello',
      protocolVersion: PROTOCOL_VERSION,
      critter: { ...opts.initialCritter },
      ...(token ? { sessionToken: token } : {}),
    };
    safeSend(hello);
  }

  function safeSend(msg: object): void {
    if (socket.readyState !== socket.OPEN) return;
    try {
      socket.send(JSON.stringify(msg));
    } catch {
      /* socket may be transitioning — drop */
    }
  }

  // Move throttle.
  let pendingMove: { x: number; y: number } | null = null;
  let lastMoveSent = 0;
  const moveTicker = window.setInterval(() => {
    if (!pendingMove) return;
    const now = Date.now();
    if (now - lastMoveSent < MOVE_INTERVAL_MS) return;
    lastMoveSent = now;
    const m: ClientMove = { type: 'client_move', x: pendingMove.x, y: pendingMove.y, t: now };
    safeSend(m);
    pendingMove = null;
  }, MOVE_INTERVAL_MS);

  // Heartbeat.
  const pingTicker = window.setInterval(() => {
    if (status !== 'connected') return;
    const p: ClientPing = { type: 'client_ping', t: Date.now() };
    safeSend(p);
  }, PING_INTERVAL_MS);

  socket.addEventListener('open', () => {
    setStatus('connecting');
    sendHello();
  });

  socket.addEventListener('close', () => {
    // Partysocket will reconnect on its own unless we manually disconnected.
    // If we never finished the welcome handshake this still reflects accurately.
    if (status !== 'offline') setStatus('reconnecting');
  });

  socket.addEventListener('error', () => {
    // Don't change status here — 'close' will follow and handle it.
  });

  socket.addEventListener('message', (e) => {
    let parsed: ServerMessage;
    try {
      parsed = JSON.parse(typeof e.data === 'string' ? e.data : '') as ServerMessage;
    } catch {
      return;
    }
    if (!parsed || typeof parsed !== 'object' || typeof parsed.type !== 'string') return;

    // Intercept welcome to capture session token + flip to 'connected'.
    if (parsed.type === 'server_welcome') {
      writeSessionToken(parsed.sessionToken);
      setStatus('connected');
    }
    // Fatal error → clear the session token (it's no good if the server
    // rejected us) and don't auto-reconnect into a loop.
    if (parsed.type === 'server_error' && parsed.fatal) {
      clearSessionToken();
      setStatus('offline');
      socket.close();
    }
    opts.onServerMessage(parsed);
  });

  return {
    status: () => status,
    sendMove(x, y) {
      pendingMove = { x, y };
    },
    sendChat(phraseIndex) {
      if (status !== 'connected') return;
      const m: ClientChat = { type: 'client_chat', phrase: phraseIndex };
      safeSend(m);
    },
    sendCosmetic(color, hat) {
      if (status !== 'connected') return;
      const m: ClientCosmeticUpdate = { type: 'client_cosmetic_update', color, hat };
      safeSend(m);
    },
    sendScore(minigame, score) {
      if (status !== 'connected') return;
      const m: ClientMinigameScore = { type: 'client_minigame_score', minigame, score };
      safeSend(m);
    },
    sendEmote(name) {
      if (status !== 'connected') return;
      const m: ClientEmote = { type: 'client_emote', emote: name };
      safeSend(m);
    },
    disconnect() {
      setStatus('offline');
      window.clearInterval(moveTicker);
      window.clearInterval(pingTicker);
      socket.close();
    },
  };
}

/** No-op client used when no host is configured; everything is a silent drop. */
function offlineStub(): NetClient {
  return {
    status: () => 'offline',
    sendMove() {},
    sendChat() {},
    sendCosmetic() {},
    sendScore() {},
    sendEmote() {},
    disconnect() {},
  };
}
